"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, FileCheck2, PhoneCall, ShieldAlert, Wind } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const hazardLocations = [{
  title: "Asbestos-containing materials",
  items: "Acoustic popcorn ceilings, pipe elbow insulation, steam boiler wrapping, and 9-inch vinyl floor tiles common in pre-1980 regional builds.",
  indicator: "Common in MD & DC pre-1980 builds"
}, {
  title: "Lead architectural components",
  items: "Pre-1978 interior and exterior window trim, wooden sills, baseboards, exterior siding, and aging municipal plumbing supply lines.",
  indicator: "Prevalent across historic DC & VA structures"
}];
const containmentSteps = [{
  title: "Critical poly isolation",
  description: "Floor-to-ceiling 6-mil polyethylene airtight barriers seal off work zones from inhabited living and office quarters."
}, {
  title: "Negative-pressure HEPA scrubbers",
  description: "Continuous directional air machines pull clean air into the zone while venting scrubbed air outside through certified 99.97% filters."
}, {
  title: "Wet-capture suppression",
  description: "Specialized surfactant misting keeps materials damp during removal, eliminating airborne friable dust and fibers at the source."
}, {
  title: "Manifested hazardous transport",
  description: "Debris is double-bagged in 6-mil labeled hazardous bags, locked in certified drums, and documented for regulated disposal."
}];
export default function Overview() {
  const prefersReducedMotion = useReducedMotion();
  const containerMotion = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.32,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="overview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Operational Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 mb-8 border-b border-border">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            <span className="font-mono text-xs font-bold uppercase tracking-wider text-primary">
              HAZARD REMEDIATION PROTOCOL • MD / DC / NOVA
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-muted-foreground">
            <span>EPA &amp; OSHA COMPLIANT STANDARDS</span>
            <span className="text-border">|</span>
            <span>DIRECT REGIONAL CREWS</span>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Context, Health Risks, Procedures */}
          <motion.div className="lg:col-span-7 flex flex-col gap-6" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={containerMotion}>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground leading-tight">
                Identifying and containing hazardous materials in older Mid-Atlantic properties.
              </h2>
              <p className="mt-3 text-lg text-muted-foreground leading-relaxed">
                Understand where legacy asbestos and lead hide in regional buildings, and how certified negative-pressure containment prevents toxic exposure during repairs or remodeling.
              </p>
            </div>

            <div className="text-foreground/90 space-y-4 text-base leading-relaxed">
              <p>
                From historic rowhomes in Washington, DC, to mid-century properties across Montgomery County and Northern Virginia, older construction routinely harbors mineral fibers and heavy metals. Undisturbed, these materials pose low day-to-day risk. When disturbed by plumbing leaks, pipe bursts, wall demolition, or standard remodeling, they release microscopic particles into indoor HVAC airflow.
              </p>
              <p>
                Asbestos fibers lodge deep in lung tissue, creating permanent scarring, asbestosis, and mesothelioma years after contact. Lead dust from disturbed paint settles on horizontal surfaces, where routine inhalation or hand-to-mouth transfer disrupts central nervous system development in children and triggers severe cardiovascular and neurological issues in adults.
              </p>
            </div>

            {/* Where Hazards Reside Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {hazardLocations.map((loc, i) => <Card key={loc.title} data-index={i} className="bg-muted/60 border-border shadow-xs hover:border-primary/40 transition-colors">
                  <CardContent className="p-5 flex flex-col justify-between h-full gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                        <AlertTriangle className="size-4 shrink-0 text-amber-600" />
                        <span>{loc.title}</span>
                      </div>
                      <p className="mt-2 text-xs sm:text-sm text-muted-foreground leading-normal">
                        {loc.items}
                      </p>
                    </div>
                    <span className="font-mono text-2xs text-foreground/70 tracking-wide uppercase">
                      {loc.indicator}
                    </span>
                  </CardContent>
                </Card>)}
            </div>

            {/* Step-by-Step TriServe Containment Protocol */}
            <div className="mt-4 pt-6 border-t border-border">
              <div className="flex items-center gap-2 mb-4">
                <ShieldAlert className="size-5 text-primary" />
                <h3 className="text-xl font-bold text-foreground">
                  Our certified containment sequence
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {containmentSteps.map((step, i) => <div key={step.title} data-index={i} className="p-4 rounded-md border border-border bg-card shadow-2xs flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-primary shrink-0" />
                      <h4 className="font-semibold text-sm text-foreground">
                        {step.title}
                      </h4>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed pl-6">
                      {step.description}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* Direct Dispatch Card */}
            <div className="mt-2 p-5 rounded-md bg-secondary text-secondary-foreground flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <span className="font-mono text-xs uppercase tracking-wider text-accent font-semibold">
                  Suspect Asbestos or Lead Contamination?
                </span>
                <p className="text-sm text-secondary-foreground/90 mt-1">
                  Local crews handle testing, containment, and disposal without call centers.
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <a href="tel:855-435-6631">
                    <PhoneCall className="size-4" />
                    <span>855-435-6631</span>
                  </a>
                </Button>
                <Button asChild variant="outline" className="border-secondary-foreground/30 text-secondary-foreground bg-transparent hover:bg-secondary-foreground/10">
                  <Link to="/ContactRequestServicePage">
                    Schedule inspection
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Visual Documentary Imagery & Field Verification */}
          <motion.div className="lg:col-span-5 flex flex-col gap-6" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={containerMotion}>
            {/* Field Image Container with Industrial Hairline Framing */}
            <div className="relative rounded-md border border-border bg-muted overflow-hidden shadow-md">
              <div className="relative aspect-4/3 w-full bg-muted overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sealed_containment_abatement_zone.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sealed_containment_abatement_zone.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sealed_containment_abatement_zone.png-wvc-sizes" alt="Sealed negative pressure plastic containment barrier with HEPA ducting in an active abatement zone" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                
                {/* Visual Scrim Overlay for Contrast */}
                <div className="absolute inset-0 bg-linear-to-t from-slate-950/80 via-transparent to-transparent pointer-events-none" />

                {/* Micro-badge for On-Site Equipment */}
                <div className="absolute top-3 left-3 bg-foreground/90 text-background px-2.5 py-1 rounded text-2xs font-mono tracking-wider flex items-center gap-1.5 backdrop-blur-xs">
                  <Wind className="size-3 text-accent" />
                  <span>ACTIVE NEGATIVE-PRESSURE CONTAINMENT</span>
                </div>

                {/* Bottom Caption Inside Frame */}
                <div className="absolute bottom-3 inset-x-3 text-background">
                  <p className="text-xs font-semibold">
                    Field Deployment: Sealed 6-mil Polyethylene Isolation
                  </p>
                  <p className="text-2xs text-background/80 mt-0.5">
                    Continuous HEPA filtration prevents fugitive fiber migration into adjacent building rooms.
                  </p>
                </div>
              </div>
            </div>

            {/* Verification & Compliance Checklist Card */}
            <Card className="border-border bg-card shadow-xs">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <FileCheck2 className="size-5 text-primary" />
                  <h4 className="font-bold text-base text-foreground">
                    Regional compliance documentation
                  </h4>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Every abatement phase is logged for insurance adjusters, municipal building inspectors, and real estate disclosures across Maryland, Washington, DC, and Virginia.
                </p>

                <div className="space-y-2.5 border-t border-border pt-4">
                  <div className="flex items-start gap-2.5 text-xs text-foreground/90">
                    <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                    <span>Certified laboratory air clearance sampling following final cleanup</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-foreground/90">
                    <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                    <span>Chain-of-custody hazardous transport manifests for authorized landfills</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-foreground/90">
                    <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                    <span>Direct coordination with homeowner and commercial insurance adjusters</span>
                  </div>
                </div>

                <div className="pt-2">
                  <div className="rounded-md bg-muted p-3 text-xs font-mono text-foreground/80 flex items-center justify-between">
                    <span>TRI-SERVE REGIONAL HQ</span>
                    <span className="text-primary font-semibold">24/7 DISPATCH READY</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Informational Notice */}
            <div className="p-4 rounded-md border border-border bg-muted/40 text-xs text-muted-foreground leading-normal flex items-start gap-3">
              <div className="p-1.5 rounded bg-primary/10 text-primary shrink-0">
                <ShieldAlert className="size-4" />
              </div>
              <p>
                Do not sand, scrape, drill, or tear down suspected drywall, ceiling textures, or pipe insulation prior to certified testing. TriServe technicians take safe bulk core samples without spreading friable fibers.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}